import * as React from 'react';
import { Icon } from '../icon/Icon';
import { mcExport } from '@mc/react-ui-icons';
import { IData } from '../virtual-data/VirtualData';
import { Button } from '../button/Button';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { modalFormDialog } from '../form/utils';
import { ModalComponentProps } from '../modal/ModalManager';
import { Dialog } from '../dialog/Dialog';
import { DialogBody } from '../dialog/DialogBody';
import { Card } from '../card/Card';
import { DialogFooter } from '../dialog/DialogFooter';
import { CloseButton } from '../button/CloseButton';
import { Block } from '../block/Block';
import { Modal } from '../modal/Modal';
import {
  Col,
  FinalTableProps,
  Row,
  showApiError,
  showUnknownApiError,
  showValidationApiError,
  VF,
  WaitingModal,
} from '../index';
import { FormRenderProps } from 'react-final-form';
import { formatNumber } from '@mc/react-format';
import {
  allColumnsIcon,
  allRowsIcon,
  checkedRowsIcon,
  noColumnsAllRowsIcon,
  noColumnsCheckedRowsIcon,
  visibleColumnsIcon,
} from './TableExportIcons';
import {
  getDataFilterExportDownloadUrl,
  DataRequestType,
  DataRequestVO,
} from '@mc/data-filter-react';
import { useAppConfig } from '@mc/react-app-base';
import { ApiError } from '@mc/axios-api';

function evaluateFileNameTemplate(template: string, date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());

  return template
    .replace('{date}', `${yyyy}-${mm}-${dd}`)
    .replace('{time}', `${hh}-${min}`)
    .replace('{date-time}', `${yyyy}-${mm}-${dd}-${hh}-${min}`);
}

export type Props<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
};

export const TableExport = <
  DATA extends IData<ID>,
  FILTER extends unknown,
  ID extends unknown,
>({
  finalTableProps,
}: Props<DATA, FILTER, ID>) => {
  const appConfig = useAppConfig();

  const handleExcelExport = () => {
    Modal.show(({ onClose }) => {
      let checkedRowsCount = finalTableProps.checkedIds?.length ?? 0;
      if (checkedRowsCount === finalTableProps.totalElements) {
        checkedRowsCount = 0;
      }

      return (
        <ExcelExportModalForm
          initialValues={{
            rowsFilter: checkedRowsCount === 0 ? 'all' : 'checked',
            columnsFilter:
              finalTableProps.columnsOrder.length ===
              finalTableProps.visibleColumns.length
                ? 'all'
                : 'visible',
          }}
          totalRowsCount={finalTableProps.totalElements ?? 0}
          checkedRowsCount={checkedRowsCount}
          visibleColumnsCount={finalTableProps.visibleColumns.length}
          totalColumnsCount={finalTableProps.columnsOrder.length}
          onClose={onClose}
          onSubmit={async (data) => {
            // @ts-ignore
            const request: DataRequestVO<FILTER> =
              finalTableProps.getExportDataRequest();
            request.requestType = DataRequestType.EXPORT_EXCEL;
            request.excelExportRequest = {};

            // Columns
            request.excelExportRequest.columnsOrder =
              finalTableProps.columnsOrder;
            switch (data.columnsFilter) {
              case 'all':
                break;
              case 'visible':
                request.excelExportRequest.visibleColumns =
                  finalTableProps.visibleColumns;
                break;
            }

            // Rows
            switch (data.rowsFilter) {
              case 'all':
                break;
              case 'checked':
                request.idsRequest = {
                  // @ts-ignore
                  ids: finalTableProps.checkedIds || [],
                };
                break;
            }

            // Název souboru
            if (finalTableProps.config.exportable?.fileName) {
              const fileNameTemplate =
                finalTableProps.config.exportable.fileName;
              const date = new Date();
              const fileName = evaluateFileNameTemplate(fileNameTemplate, date);
              request.excelExportRequest.fileName = fileName;
            }

            // ---

            try {
              const result = await Modal.showWaiting(
                finalTableProps.config.api(request, {
                  hardFilter: finalTableProps.hardFilter,
                }),
                () => <WaitingModal title={'Probíhá export do XLS...'} />
              );

              Modal.hide();

              result.exportFileId &&
                window.open(
                  appConfig.serverContextPath +
                    getDataFilterExportDownloadUrl(result.exportFileId),
                  '_blank'
                );
            } catch (_error: any) {
              if (_error.stack && _error.message) {
                console.error(_error);

                await showUnknownApiError();
              } else {
                const error = _error as ApiError;

                // Request byl programově přerušen
                if (!error.cancelled) {
                  switch (error.status) {
                    case 422:
                      await showValidationApiError(error);
                      break;
                    default:
                      await showApiError(error);
                      break;
                  }
                }
              }
            }
          }}
        />
      );
    });
  };

  // return (
  //     <Button
  //       style={'link'}
  //       icon={<Icon icon={mcExport} />}
  //       title={'Exportovat'}
  //       menu={
  //         <Menu>
  //           <MenuItem
  //             icon={<Icon icon={mcFileExcel} />}
  //             onClick={handleExcelExport}
  //             text={'Export do XLS'}
  //           />
  //         </Menu>
  //       }
  //     />
  // );

  const xls =
    finalTableProps.config.exportable?.all ||
    finalTableProps.config.exportable?.excel;

  return (
    <>
      {xls && (
        <Button
          style={'link'}
          icon={<Icon icon={mcExport} />}
          title={'Export do XLS'}
          onClick={handleExcelExport}
        />
      )}
    </>
  );
};

type ExcelExportModalProps<FORM> = {
  checkedRowsCount: number;
  totalRowsCount: number;
  visibleColumnsCount: number;
  totalColumnsCount: number;
  formRenderProps: FormRenderProps<FORM>;
} & ModalComponentProps;

type ExcelExportVO = {
  rowsFilter?: 'all' | 'checked';
  columnsFilter?: 'all' | 'visible';
};

const ColumnsField = ({
  showVisibleColumns,
  submitting,
  value,
}: {
  showVisibleColumns: boolean;
  submitting: boolean;
  value?: 'all' | 'visible';
}) => (
  <VF.RadioGroup
    required={showVisibleColumns}
    name={'columnsFilter'}
    label={'Sloupce'}
  >
    <Row>
      {showVisibleColumns && (
        <Col>
          <VF.Radio
            key={'visible'}
            label={`Aktuálně viditelné`}
            name={'columnsFilter'}
            value={'visible'}
          />
          <div
            style={{ opacity: value === 'visible' ? 1 : 0.6 }}
            className={'mt-1'}
          >
            {visibleColumnsIcon}
          </div>
        </Col>
      )}
      <Col>
        <VF.Radio
          key={'all'}
          disabled={submitting || !showVisibleColumns}
          label={`Vše`}
          name={'columnsFilter'}
          value={'all'}
        />
        {showVisibleColumns && (
          <div
            style={{ opacity: value === 'all' ? 1 : 0.6 }}
            className={'mt-1'}
          >
            {allColumnsIcon}
          </div>
        )}
      </Col>
    </Row>
  </VF.RadioGroup>
);

const RowsField = ({
  showCheckedRows,
  checkedRowsCount,
  showVisibleColumns,
  submitting,
  value,
}: {
  showCheckedRows: boolean;
  checkedRowsCount: number;
  showVisibleColumns: boolean;
  submitting: boolean;
  value?: 'all' | 'checked';
}) => (
  <VF.RadioGroup
    required={showCheckedRows}
    name={'rowsFilter'}
    label={'Záznamy'}
  >
    <Row>
      {showCheckedRows && (
        <Col>
          <VF.Radio
            key={'all'}
            label={`Vybrané (${formatNumber(checkedRowsCount)})`}
            name={'rowsFilter'}
            value={'checked'}
          />
          <div
            style={{ opacity: value === 'checked' ? 1 : 0.6 }}
            className={'mt-1'}
          >
            {showVisibleColumns ? checkedRowsIcon : noColumnsCheckedRowsIcon}
          </div>
        </Col>
      )}
      <Col>
        <VF.Radio
          key={'all'}
          disabled={submitting || !showCheckedRows}
          label={`Vše`}
          name={'rowsFilter'}
          value={'all'}
        />
        {showCheckedRows && (
          <div
            style={{ opacity: value === 'all' ? 1 : 0.6 }}
            className={'mt-1'}
          >
            {showVisibleColumns ? allRowsIcon : noColumnsAllRowsIcon}
          </div>
        )}
      </Col>
    </Row>
  </VF.RadioGroup>
);

const ExcelExportModal: React.FC<ExcelExportModalProps<ExcelExportVO>> = ({
  checkedRowsCount,
  visibleColumnsCount,
  totalColumnsCount,
  formRenderProps,
  onClose,
}) => {
  const { submitting, handleSubmit } = formRenderProps;
  let showVisibleColumns =
    visibleColumnsCount > 0 && visibleColumnsCount !== totalColumnsCount;

  let showCheckedRows = checkedRowsCount > 0;

  return (
    <Dialog autoFocus={false} onClose={onClose} title={'Export do XLS'}>
      <DialogBody>
        <Card mode={'edit'}>
          {!showVisibleColumns && !showCheckedRows && (
            <Block>
              <Row>
                <Col>
                  <ColumnsField
                    value={formRenderProps.values.columnsFilter}
                    showVisibleColumns={showVisibleColumns}
                    submitting={submitting}
                  />
                </Col>
                <Col>
                  <RowsField
                    value={formRenderProps.values.rowsFilter}
                    checkedRowsCount={checkedRowsCount}
                    showCheckedRows={showCheckedRows}
                    showVisibleColumns={showVisibleColumns}
                    submitting={submitting}
                  />
                </Col>
              </Row>
            </Block>
          )}
          {(showVisibleColumns || showCheckedRows) && (
            <Block>
              <ColumnsField
                value={formRenderProps.values.columnsFilter}
                showVisibleColumns={showVisibleColumns}
                submitting={submitting}
              />
            </Block>
          )}
          {(showVisibleColumns || showCheckedRows) && (
            <Block>
              <RowsField
                value={formRenderProps.values.rowsFilter}
                checkedRowsCount={checkedRowsCount}
                showCheckedRows={showCheckedRows}
                showVisibleColumns={showVisibleColumns}
                submitting={submitting}
              />
            </Block>
          )}
        </Card>
      </DialogBody>
      <DialogFooter>
        <CloseButton onClick={onClose}>Zrušit</CloseButton>
        <Button style={'primary'} dataTest={'export'} onClick={handleSubmit}>
          Exportovat
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

const ExcelExportModalForm =
  // @ts-ignore
  modalFormDialog<ExcelExportVO, ExcelExportModalProps>(ExcelExportModal, {});
